import { BaseApiService } from './BaseApiService';
import { CacheEntry, CacheManager } from '../cache/CacheManager';

type PendingFeedbackPayload = {
  data: FeedbackResponse[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  isAuthFailure?: boolean;
};

const pendingFeedbackCache = new Map<'pending', CacheEntry<PendingFeedbackPayload>>();

export interface FeedbackData {
  userId: string;
  subject: string;
  email: string;
  message: string;
  category?: 'bug' | 'feature' | 'general' | 'improvement';
  type: 'bug_report' | 'feature_request' | 'general_feedback' | 'performance_issue';
  priority?: 'low' | 'medium' | 'high';
  userAgent?: string;
  page?: string;
}

export interface FeedbackResponse {
  id: string;
  userId: string;
  email: string;
  message: string;
  timestamp: string;
  status: 'pending' | 'reviewed' | 'resolved';
  priority: 'low' | 'medium' | 'high';
  category?: 'bug' | 'feature' | 'general' | 'improvement';
  userAgent?: string;
  page?: string;
}

export interface FeedbackStats {
  total: number;
  pending: number;
  reviewed: number;
  resolved: number;
  byPriority: {
    high: number;
    medium: number;
    low: number;
  };
  byCategory: Record<string, number>;
}

/**
 * Feedback Service
 * Handles all feedback-related API operations
 */
export class FeedbackService extends BaseApiService {
  /**
   * POST /api/feedback - Submit new feedback
   */
  async submitFeedback(data: FeedbackData): Promise<{ 
    message: string; 
    feedbackId: string; 
    feedback: FeedbackResponse;
  }> {
    // Map frontend fields to backend expected fields
    const feedbackData = {
      type: data.type,
      subject: data.subject,
      message: data.message,
      userEmail: data.email, // Backend expects 'userEmail' not 'email'
      userAgent: data.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
      page: data.page || (typeof window !== 'undefined' ? window.location.href : undefined)
    };

    const response = await this.post<{
      message: string;
      feedbackId: string;
      feedback: FeedbackResponse;
    }>('/feedback', feedbackData);

    // Invalidate pending feedback cache since there is new feedback
    CacheManager.clear(pendingFeedbackCache);

    return response;
  }

  /**
   * GET /api/feedback - Get all feedback (admin endpoint)
   */
  async getAllFeedback(options?: {
    status?: 'pending' | 'reviewed' | 'resolved';
    userId?: string;
    category?: 'bug' | 'feature' | 'general' | 'improvement';
    priority?: 'low' | 'medium' | 'high';
    limit?: number;
    offset?: number;
  }): Promise<{ 
    data: FeedbackResponse[]; 
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    // Use cache only for the common `/api/feedback?status=pending` case
    const isSimplePendingQuery =
      options?.status === 'pending' &&
      !options.userId &&
      !options.category &&
      !options.priority &&
      !options.limit &&
      !options.offset;

    try {

      if (isSimplePendingQuery) {
        const cached = CacheManager.get(pendingFeedbackCache, 'pending');
        if (cached !== null) {
          if (cached.isAuthFailure) {
            return {
              data: [],
              pagination: { total: 0, limit: 50, offset: 0, hasMore: false }
            };
          }
          return {
            data: cached.data,
            pagination: cached.pagination,
          };
        }
      }

      const params = new URLSearchParams();
      
      if (options?.status) params.append('status', options.status);
      if (options?.userId) params.append('userId', options.userId);
      if (options?.category) params.append('category', options.category);
      if (options?.priority) params.append('priority', options.priority);
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());

      const url = params.toString() ? `/feedback?${params.toString()}` : '/feedback';
      const response = await this.get<{
        data: FeedbackResponse[];
        pagination: {
          total: number;
          limit: number;
          offset: number;
          hasMore: boolean;
        };
      }>(url);
      
      const safeResponse = response || { data: [], pagination: { total: 0, limit: 50, offset: 0, hasMore: false } };

      // Populate cache for simple pending query
      if (isSimplePendingQuery) {
        CacheManager.set(
          pendingFeedbackCache,
          'pending',
          {
            data: safeResponse.data,
            pagination: safeResponse.pagination,
          },
          CacheManager.ttl.feedbackPending
        );
      }

      return safeResponse;
    } catch (error) {
      // Handle permission errors gracefully
      if (error instanceof Error && (error.message.includes('Access denied') || error.message.includes('Authentication required'))) {
        console.log('🚫 [FEEDBACK] User lacks admin permissions for feedback management - caching failure');
        
        // Cache the auth failure for simple pending queries to avoid repeated API calls
        if (isSimplePendingQuery) {
          CacheManager.set(
            pendingFeedbackCache,
            'pending',
            {
              data: [],
              pagination: { total: 0, limit: 50, offset: 0, hasMore: false },
              isAuthFailure: true
            },
            CacheManager.ttl.feedbackPending
          );
        }
        
        return {
          data: [],
          pagination: { total: 0, limit: 50, offset: 0, hasMore: false }
        };
      }
      throw error;
    }
  }

  /**
   * GET /api/feedback/stats - Get feedback statistics
   */
  async getFeedbackStats(): Promise<FeedbackStats> {
    try {
      const response = await this.get<FeedbackStats>('/feedback/stats');
      return response || {
        total: 0,
        pending: 0,
        reviewed: 0,
        resolved: 0,
        byPriority: { high: 0, medium: 0, low: 0 },
        byCategory: {}
      };
    } catch (error) {
      // Handle permission errors gracefully
      if (error instanceof Error && error.message.includes('Access denied')) {
        console.log('🚫 [FEEDBACK] User lacks admin permissions for feedback stats');
        return {
          total: 0,
          pending: 0,
          reviewed: 0,
          resolved: 0,
          byPriority: { high: 0, medium: 0, low: 0 },
          byCategory: {}
        };
      }
      throw error;
    }
  }

  /**
   * GET /api/feedback/:id - Get specific feedback by ID
   */
  async getFeedbackById(id: string): Promise<FeedbackResponse | null> {
    try {
      return await this.get<FeedbackResponse>(`/feedback/${id}`);
    } catch (error) {
      // Handle permission errors gracefully
      if (error instanceof Error && error.message.includes('Access denied')) {
        console.log('🚫 [FEEDBACK] User lacks admin permissions for feedback details');
        return null;
      }
      return null;
    }
  }

  /**
   * PUT /api/feedback/:id/status - Update feedback status (admin endpoint)
   */
  async updateFeedbackStatus(id: string, status: 'pending' | 'reviewed' | 'resolved'): Promise<{
    message: string;
    feedback: FeedbackResponse;
  }> {
    try {
      const response = await this.put<{
        message: string;
        feedback: FeedbackResponse;
      }>(`/feedback/${id}/status`, { status });

      // Status changes can affect the pending list, so invalidate cache
      CacheManager.clear(pendingFeedbackCache);

      return response;
    } catch (error) {
      // Handle permission errors gracefully
      if (error instanceof Error && error.message.includes('Access denied')) {
        console.log('🚫 [FEEDBACK] User lacks admin permissions to update feedback status');
        throw new Error('You do not have permission to update feedback status');
      }
      throw error;
    }
  }

  /**
   * GET /api/feedback/user/:userId - Get feedback for current user
   */
  async getUserFeedback(userId: string, limit: number = 10): Promise<FeedbackResponse[]> {
    const response = await this.get<{ data: FeedbackResponse[]; count: number }>(`/feedback/user/${userId}?limit=${limit}`);
    return response?.data || [];
  }
}
